import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    Box, Button, TextField, MenuItem, Autocomplete, Checkbox, FormControlLabel,
    Typography, IconButton, Chip, Tooltip, TableRow, TableCell, Snackbar, Alert 
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import * as API from '../../api';
import DataTable from '../DataTable';
import CustomDatePicker from '../CustomDatePicker';
import BillDetailDialog from '../BillDetailDialog';
import ReportExportDialog from './ReportExportDialog';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatDateDisplay } from '../../utils/dateUtils';
import PaidIcon from '@mui/icons-material/Paid'; // Thêm icon
import BulkPaymentDialog from '../BulkPaymentDialog'; // Thêm Component

const CustomLabel = ({ title }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title}
    </Typography>
);

const CashflowTab = forwardRef((props, ref) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // --- STATE BỘ LỌC ---
    const [uiStartDate, setUiStartDate] = useState(searchParams.get('start') || format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [uiEndDate, setUiEndDate] = useState(searchParams.get('end') || format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [uiIsAllTime, setUiIsAllTime] = useState(searchParams.get('all_time') === 'true');
    const [uiFlowType, setUiFlowType] = useState(searchParams.get('flow_type') || 'ALL');
    const [appliedFlowType, setAppliedFlowType] = useState(searchParams.get('flow_type') || 'ALL');
    const [uiSelectedAccount, setUiSelectedAccount] = useState({ id: 'ALL', name: 'Tất cả tài khoản', bank_name: '' });
    const [appliedDistributionStatus, setAppliedDistributionStatus] = useState('ALL');
    
    // --- STATE DATA ---
    const [accounts, setAccounts] = useState([]);
    const [cashflowData, setCashflowData] = useState([]);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [viewBillModal, setViewBillModal] = useState({ open: false, billData: null, bookingData: null });
    const [reportOpen, setReportOpen] = useState(false);
    const [uiDistributionStatus, setUiDistributionStatus] = useState('ALL'); 
    const [selectedTxKeys, setSelectedTxKeys] = useState([]);
    const [bulkPayModal, setBulkPayModal] = useState({ open: false, items: [], type: 'UNIFIED', distributionSummary: null });

    const showToast = (msg, severity = 'success') => setToast({ open: true, message: msg, severity });

    useEffect(() => {
        if (props.setCanExport) props.setCanExport(true); // Bật nút Export ở Dashboard
        loadData();
    }, []);

    useImperativeHandle(ref, () => ({
        resetFilters: () => {
            const defaultAcc = { id: 'ALL', name: 'Tất cả tài khoản', bank_name: '' };
            setUiStartDate(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
            setUiEndDate(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
            setUiIsAllTime(false);
            setUiFlowType('ALL');
            setUiSelectedAccount(defaultAcc);
            setUiDistributionStatus('ALL');
            setAppliedDistributionStatus('ALL');
            setSelectedTxKeys([]);
            loadData({ selectedAccount: defaultAcc, isAllTime: false });
        },
        loadData: () => loadData(),
        openExportDialog: () => setReportOpen(true)
    }));

    const loadData = async (overrides = {}) => {
        try {
            let currentAccounts = accounts;
            if (accounts.length === 0) {
                const accRes = await API.fetchAllFinancialAccounts();
                setAccounts(accRes.data);
                currentAccounts = accRes.data;
            }

            const isAllTime = overrides.hasOwnProperty('isAllTime') ? overrides.isAllTime : uiIsAllTime;
            const start = overrides.startDate || uiStartDate;
            const end = overrides.endDate || uiEndDate;
            const flowType = overrides.hasOwnProperty('flowType') ? overrides.flowType : uiFlowType;
            setAppliedFlowType(flowType);

            let targetAccount = overrides.hasOwnProperty('selectedAccount') ? overrides.selectedAccount : uiSelectedAccount;
            
            const urlAccId = searchParams.get('account_id');
            if (!overrides.selectedAccount && urlAccId && targetAccount.id === 'ALL' && urlAccId !== 'ALL') {
                    const found = currentAccounts.find(a => a.id.toString() === urlAccId);
                    if (found) {
                        targetAccount = found;
                        setUiSelectedAccount(found);
                    }
            }

            if (targetAccount) {
                const params = {};
                if (!isAllTime) {
                    params.start_date = start;
                    params.end_date = end;
                }
                let res;
                if (targetAccount.id === 'ALL') {
                    res = await API.client.get('/financial-accounts/all_transactions/', { params });
                } else {
                    res = await API.fetchAccountTransactions(targetAccount.id, params);
                }
                setCashflowData(res.data);
            }

            const distStatus = overrides.hasOwnProperty('distributionStatus') ? overrides.distributionStatus : uiDistributionStatus;
            setAppliedDistributionStatus(distStatus);

        } catch (error) {
            console.error(error);
            showToast("Lỗi tải dữ liệu dòng tiền", "error");
        }
    };

    const filteredCashflowData = cashflowData.filter(item => {
        // 1. Lọc theo loại GD (đã có appliedFlowType)
        if (appliedFlowType !== 'ALL') {
            if (appliedFlowType === 'IN' && item.type !== 'Thu') return false;
            if (appliedFlowType === 'OUT' && item.type !== 'Chi') return false;
            if (appliedFlowType !== 'IN' && appliedFlowType !== 'OUT' && item.type !== appliedFlowType) return false;
        }
        
        // 2. SỬA TẠI ĐÂY: Lọc theo trạng thái phân bổ đã áp dụng
        if (appliedDistributionStatus !== 'ALL') {
            if (item.distribution_status !== appliedDistributionStatus) return false;
        }
        
        if (item.distribution_status === 'NOT_APPLICABLE') return false;

        return true;
    }).map(item => ({
        ...item,
        txKey: `${item.tx_type}_${item.original_id}`
    }));

    const handleBulkDistribute = () => {
        if (selectedTxKeys.length === 0) return;

        const selectedRows = filteredCashflowData.filter(r => selectedTxKeys.includes(r.txKey));

        let totalRevenue = 0;
        let totalCost = 0;
        const payers = {}; // Lưu số tiền cổ đông đã thu/chi cá nhân
        const companyHoldings = { paid: 0, received: 0 }; // Lưu số tiền quỹ công ty đã thu/chi

        selectedRows.forEach(row => {
            const amt = Math.abs(parseFloat(row.amount) || 0);
            
            if (row.tx_type === 'PAYMENT') { // Dòng tiền THU VÀO
                totalRevenue += amt;
                if (row.acc_type === 'PERSONAL' && row.owner_id) {
                    if (!payers[row.owner_id]) payers[row.owner_id] = { paid: 0, received: 0 };
                    payers[row.owner_id].received += amt;
                } else {
                    // Mặc định các tài khoản không phải PERSONAL sẽ vào quỹ COMPANY
                    companyHoldings.received += amt;
                }
            } else if (row.tx_type === 'PAYABLE') { // Dòng tiền CHI RA
                totalCost += amt;
                if (row.acc_type === 'PERSONAL' && row.owner_id) {
                    if (!payers[row.owner_id]) payers[row.owner_id] = { paid: 0, received: 0 };
                    payers[row.owner_id].paid += amt;
                } else {
                    companyHoldings.paid += amt;
                }
            }
        });

        const distributionSummary = {
            totalRevenue,
            totalCost,
            totalProfit: totalRevenue - totalCost,
            payers: payers, 
            companyHoldings: companyHoldings
        };

        setBulkPayModal({
            open: true,
            items: selectedRows.map(row => ({
                id: row.original_id,
                type: row.tx_type,
                booking_code: row.booking_code
            })),
            type: 'CASHFLOW_DISTRIBUTION', 
            distributionSummary: distributionSummary
        });
    };

    const handleViewDistributionBill = (row) => {
        if (!row.distribution_session) {
            return showToast("Không tìm thấy chi tiết phiên phân bổ", "error");
        }

        const session = row.distribution_session;
        const transactions = session.transactions || [];
        const snapshot = session.shareholder_snapshot || null;

        if (transactions.length === 0) {
            return showToast("Phiên phân bổ này chưa có giao dịch chuyển tiền nào", "warning");
        }

        // Tạo summary giả lập để hiển thị Header Modal
        const summary = {
            totalRevenue: row.tx_type === 'PAYMENT' ? Math.abs(row.amount) : 0,
            totalCost: row.tx_type === 'PAYABLE' ? Math.abs(row.amount) : 0,
            totalProfit: row.tx_type === 'PAYMENT' ? Math.abs(row.amount) : -Math.abs(row.amount),
            payers: {}
        };

        const savedTransactions = transactions.map(d => {
            let senderObj = { type: 'UNKNOWN', label: '---' };
            if (d.sender_account) senderObj = { type: 'COMPANY', label: d.sender_name };
            else if (d.sender_shareholder) senderObj = { type: 'SHAREHOLDER', label: d.sender_name };

            let receiverObj = { type: 'UNKNOWN', label: '---' };
            if (d.receive_account) receiverObj = { type: 'COMPANY', label: d.receiver_name };
            else if (d.receiver_shareholder) receiverObj = { type: 'SHAREHOLDER', label: d.receiver_name };

            return {
                id: d.id,
                sender: senderObj,
                receiver: receiverObj,
                amount: Math.abs(parseFloat(d.amount)), 
                note: d.note,
                fileUrl: d.bill_image,
                senderLabel: senderObj.label,
                receiverLabel: receiverObj.label,
                transactionDate: d.created_at
            };
        });

        setBulkPayModal({
            open: true,
            items: [row], 
            type: 'CASHFLOW_DISTRIBUTION', 
            distributionSummary: summary,
            readOnly: true,
            savedTransactions: savedTransactions,
            snapshotData: snapshot 
        });
    };

    const totalCashflowAmount = filteredCashflowData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const cashflowSummary = { dest_account: 'Tổng', amount: formatCurrency(totalCashflowAmount) };

    const columns = [
        { 
            id: 'select', 
            label: (
                <Checkbox 
                    size="small" 
                    checked={filteredCashflowData.length > 0 && selectedTxKeys.length === filteredCashflowData.filter(r => r.distribution_status === 'PENDING').length}
                    indeterminate={selectedTxKeys.length > 0 && selectedTxKeys.length < filteredCashflowData.filter(r => r.distribution_status === 'PENDING').length}
                    onChange={(e) => {
                        if (e.target.checked) setSelectedTxKeys(filteredCashflowData.filter(r => r.distribution_status === 'PENDING').map(r => r.txKey));
                        else setSelectedTxKeys([]);
                    }}
                    sx={{ color: '#007373' }}
                />
            ),
            minWidth: 50, align: 'center' 
        },
        { id: 'date', label: 'Ngày', minWidth: 100 },
        { id: 'ref', label: 'Mã BK', maxWidth: 100 },
        { id: 'customer_name', label: 'KH', maxWidth: 150 },
        { id: 'type', label: 'Loại GD', maxWidth: 80 },
        { id: 'source_account', label: 'TK Gửi', maxWidth: 140 },
        { id: 'dest_account', label: 'TK Nhận', maxWidth: 140 },
        { id: 'amount', label: 'Số tiền', maxWidth: 120, align: 'right' },
        { id: 'distribution', label: 'TT', maxWidth: 120, align: 'center' },
        { id: 'action', label: '', maxWidth: 120, align: 'center' }
    ];

    const renderRow = (row) => {
        let color = '#333';
        if (row.type === 'Thu') color = '#2e7d32';
        else if (row.type === 'Chi') color = '#d32f2f';
        else if (row.type === 'Thu hộ') color = '#9c27b0';
        else if (row.type === 'Hoàn tiền') color = '#ed6c02';

        const isPending = row.distribution_status === 'PENDING';

        return (
            <TableRow key={row.txKey} hover selected={selectedTxKeys.includes(row.txKey)}>
                <TableCell align="center">
                    <Checkbox size="small" 
                        disabled={!isPending}
                        checked={selectedTxKeys.includes(row.txKey)}
                        onChange={() => setSelectedTxKeys(prev => prev.includes(row.txKey) ? prev.filter(x => x !== row.txKey) : [...prev, row.txKey])}
                        sx={{ color: '#007373' }}
                    />
                </TableCell>
                <TableCell>{formatDateDisplay(row.date)}</TableCell>
                <TableCell>{row.ref || row.booking_code}</TableCell>
                <TableCell>{row.customer_name}</TableCell>
                <TableCell><Chip label={row.type} size="small" style={{ color, borderColor: color }} variant="outlined" /></TableCell>
                <TableCell>{row.source_account}</TableCell>
                <TableCell>{row.dest_account}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: color }}>{formatCurrency(row.amount)}</TableCell>
                <TableCell align="center">
                    {row.distribution_status === 'DONE' ? (
                        <Chip label="Đã chia" color="success" size="small" variant="outlined" />
                    ) : (
                        <Chip label="Chưa chia" color="default" size="small" variant="outlined" />
                    )}
                </TableCell>
                <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {/* Nút Xem Phân bổ (Màu xanh lá) - Chỉ hiện khi Đã PB */}
                        {row.distribution_status === 'DONE' && (
                            <Tooltip title="Xem Lịch sử Phân bổ">
                                <IconButton size="small" color="success" onClick={() => handleViewDistributionBill(row)}>
                                    <PaidIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        
                        {/* Nút Xem Bill Giao Dịch Gốc (Màu xanh biển) */}
                        <Tooltip title="Xem Bill Thu/Chi">
                            <span>
                                <IconButton size="small" color="primary" disabled={!row.bill_image} onClick={() => { 
                                     setViewBillModal({
                                        open: true,
                                        bookingData: { booking_code: row.booking_code || row.ref, customer_name: row.customer_name }, 
                                        billData: {
                                            type: `${row.type}`, amount: Math.abs(row.amount),
                                            note: row.note, imageUrl: row.bill_image, 
                                            transaction_date: row.transaction_date || row.date,
                                        }
                                    });
                                }}>
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        {/* Nút Xem Booking */}
                        <Tooltip title="Xem Booking">
                            <IconButton size="small" color="info" onClick={() => navigate('/form', { state: { bookingCodeToLoad: row.booking_code || row.ref } })}>
                                <ArrowForwardIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    const reportColumns = [
        { header: 'Ngày', width: 10, accessor: 'date', render: r => formatDateDisplay(r.date) },
        { header: 'Mã BK', width: 10, accessor: 'ref', render: r => r.ref || r.booking_code },
        { header: 'Khách hàng', width: 15, accessor: 'customer_name' },
        { header: 'Loại GD', width: 10, accessor: 'type' },
        { header: 'TK Gửi', width: 12, accessor: 'source_account' },
        { header: 'TK Nhận', width: 12, accessor: 'dest_account' },
        { header: 'Số tiền', width: 10, accessor: 'amount', render: r => formatCurrency(r.amount) },
        { header: 'Nội dung', width: 21, accessor: 'note' }
    ];

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box sx={{ paddingTop: '28px' }}>
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={uiIsAllTime} 
                                onChange={e => setUiIsAllTime(e.target.checked)} 
                                size="small" 
                                sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' } }}
                            />
                        }
                        label={<Typography variant="body2" sx={{ fontWeight: 'bold' }}>Toàn thời gian</Typography>}
                    />
                </Box>
                <Box sx={{ width: '150px' }}><CustomLabel title="Từ ngày" /><CustomDatePicker value={uiStartDate} onChange={e => setUiStartDate(e.target.value)} disabled={uiIsAllTime} /></Box>
                <Box sx={{ width: '150px' }}><CustomLabel title="Đến ngày" /><CustomDatePicker value={uiEndDate} onChange={e => setUiEndDate(e.target.value)} disabled={uiIsAllTime} /></Box>
                <Box sx={{ width: 160 }}>
                    <CustomLabel title="Loại giao dịch" />
                    <TextField select fullWidth size="small" value={uiFlowType} onChange={(e) => setUiFlowType(e.target.value)}>
                        <MenuItem value="ALL">Tất cả</MenuItem>
                        <MenuItem value="IN">Thu</MenuItem>
                        <MenuItem value="OUT">Chi</MenuItem>
                        <MenuItem value="Thu hộ">Thu hộ</MenuItem>
                        <MenuItem value="Hoàn tiền">Hoàn tiền</MenuItem>
                    </TextField>
                </Box>
                <Box sx={{ width: 220 }}>
                    <CustomLabel title="Tài khoản" />
                    <Autocomplete
                        size="small"
                        options={[{ id: 'ALL', name: 'Tất cả tài khoản' }, ...accounts]}
                        getOptionLabel={(opt) => opt.name}
                        value={uiSelectedAccount}
                        onChange={(e, val) => setUiSelectedAccount(val)}
                        renderInput={(params) => <TextField {...params} placeholder="Tất cả" />}
                        isOptionEqualToValue={(o,v) => o.id === v.id}
                    />
                </Box>
                <Box sx={{ width: 160 }}>
                    <CustomLabel title="Trạng thái Phân bổ" />
                    <TextField select fullWidth size="small" value={uiDistributionStatus} onChange={(e) => setUiDistributionStatus(e.target.value)}>
                        <MenuItem value="ALL">Tất cả</MenuItem>
                        <MenuItem value="PENDING">Chưa chia</MenuItem>
                        <MenuItem value="DONE">Đã chia</MenuItem>
                    </TextField>
                </Box>
                <Box sx={{ paddingTop: '24px' }}>
                    <Button 
                        variant="contained" 
                        onClick={() => { 
                            setSelectedTxKeys([]); // Reset lại danh sách đã chọn
                            loadData(); 
                        }} 
                        sx={{ bgcolor: '#007373', fontWeight: 'bold', height: 40 }}
                    >
                        Áp dụng
                    </Button>
                </Box>
            </Box>

            {selectedTxKeys.length > 0 && (
                <Box sx={{ p: 2, mb: 2, mt: 2, bgcolor: '#e0f2f1', border: '1px solid #009688', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontWeight="bold" color="#007373">Đã chọn: {selectedTxKeys.length} giao dịch chờ phân bổ</Typography>
                    <Button variant="contained" startIcon={<PaidIcon />} onClick={handleBulkDistribute} sx={{ bgcolor: '#007373' }}>
                        TẠO PHIÊN PHÂN BỔ ({selectedTxKeys.length})
                    </Button>
                </Box>
            )}

            {!uiSelectedAccount ? (
                <Typography align="center" sx={{ py: 5, color: '#777' }}>Vui lòng chọn Tài khoản và nhấn "Áp dụng"</Typography>
            ) : (
                <DataTable columns={columns} data={filteredCashflowData} noDataText="Chưa có phát sinh giao dịch nào" renderRow={renderRow} summaryData={cashflowSummary} />
            )}

            <BulkPaymentDialog 
                open={bulkPayModal.open} 
                onClose={() => setBulkPayModal({ ...bulkPayModal, open: false })} 
                items={bulkPayModal.items} 
                type={bulkPayModal.type} 
                distributionSummary={bulkPayModal.distributionSummary} 
                showToast={showToast} 
                onSuccess={() => { setSelectedTxKeys([]); loadData(); }} 
                readOnly={bulkPayModal.readOnly}
                savedTransactions={bulkPayModal.savedTransactions}
                snapshotData={bulkPayModal.snapshotData}
            />
            <BillDetailDialog open={viewBillModal.open} onClose={() => setViewBillModal({...viewBillModal, open:false})} billData={viewBillModal.billData} bookingData={viewBillModal.bookingData} />
            {(() => {
                const totalAmount = filteredCashflowData.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
                const summaryRowData = {
                    date: 'TỔNG CỘNG:', // Khớp với accessor của cột đầu tiên
                    amount: formatCurrency(totalAmount)
                };

                return (
                    <ReportExportDialog 
                        open={reportOpen} 
                        onClose={() => setReportOpen(false)} 
                        customColumns={reportColumns} 
                        summaryRow={summaryRowData} /* <--- THÊM DÒNG TỔNG */
                        fileNamePrefix="BaoCao_DongTien" 
                        customTitle="BÁO CÁO DÒNG TIỀN" 
                        type="CASH_FLOW" 
                        data={filteredCashflowData} 
                        dateRange={{ start: uiStartDate, end: uiEndDate }} 
                    />
                );
            })()}
            <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast({...toast, open:false})} anchorOrigin={{vertical:'top', horizontal:'right'}}><Alert severity={toast.severity}>{toast.message}</Alert></Snackbar>
        </Box>
    );
});

export default CashflowTab;